import { Box, Theme, useTheme } from "@mui/material";
import type { JSX } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import "./translations";

import type { ValuationHubCoreModelRealEstateProsaWebFactorsInput } from "@/api/generatedApi";
import { AnalysisSummaryCard } from "@/components/AnalysisSummaryCard";
import { DemandScorePanel } from "@/components/demandScorePanel";
import { IndicativeMarketValuationPanel } from "@/components/indicativeMarketValuationPanel";
import { PricePositioningPanel } from "@/components/pricePositioningPanel";
import { PropertyDetailsPanel } from "@/components/propertyDetailsPanel/PropertyDetailsPanel";
import { ReportConfigurationPanel, ReportConfigurationPanelProps } from "@/components/reportConfigurationPanel";
import { getAvmDisplayedRange } from "@/helpers/avm";
import { useModuleContext } from "@/hooks/useModuleContext";
import { useAppSelector } from "@/store/hooks/useAppSelector";
import { ObjectTypeEnum } from "@/types/refineRealEstate";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  pageGrid: {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      lg: "minmax(0, 827px) 363px",
    },
    gap: theme.analysisLayout.containerGap,
    justifyContent: "center",
  },
  analysisPanelsGrid: {
    display: "grid",
    gap: theme.analysisLayout.panelGap,
  },
  summaryCard: {
    alignSelf: "start",
    position: {
      xs: "static",
      lg: "sticky",
    },
    top: {
      xs: "auto",
      lg: "0px",
    },
  },
});

interface ValuationAnalysisContentProps {
  isLoading?: boolean;
  onSavePriceRange?: (range: [number, number]) => Promise<void> | void;
  isSavingPriceRange?: boolean;
  onSaveIndicativeFactor?: (
    factor: number | ValuationHubCoreModelRealEstateProsaWebFactorsInput,
  ) => Promise<void> | void;
  isSavingIndicativeFactor?: boolean;
  onEditObjectData?: () => void;
  reportConfigurationPanelProps?: ReportConfigurationPanelProps;
  onGenerateReport?: () => void;
  onGenerateWordDocument?: () => void;
  isGenerateReportLoading?: boolean;
}

export const ValuationAnalysisContent = (props: ValuationAnalysisContentProps): JSX.Element => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const { isValuationReport } = useModuleContext();
  const navigate = useNavigate();
  const location = useLocation();
  const valuationState = useAppSelector((state) =>
    isValuationReport ? state.valuationReport : state.currentValuation,
  );
  const currentValuationId = !isValuationReport && "id" in valuationState ? valuationState.id : null;
  const avmDisplayedRange = getAvmDisplayedRange(
    valuationState.avm,
    "selectedEquipmentPriceRange" in valuationState ? valuationState.selectedEquipmentPriceRange : undefined,
  );
  const hasValuationResults =
    !!valuationState.sone?.market_value || (!!avmDisplayedRange.min && !!avmDisplayedRange.max);
  const hasReportConfigurationPanel = Boolean(props.reportConfigurationPanelProps);
  const objectType = valuationState.propertyInformation?.generalInfo.objectType;
  const showDemandScore =
    objectType !== ObjectTypeEnum.multiFamilyHouse &&
    objectType !== ObjectTypeEnum.mixedUseProperty &&
    objectType !== ObjectTypeEnum.plotOfLand;
  const isGenerateReportDisabled =
    !hasReportConfigurationPanel ||
    !hasValuationResults ||
    (!isValuationReport && "expired" in valuationState && valuationState.expired === true);

  const scrollToReportConfiguration = (): void => {
    requestAnimationFrame(() => {
      document.getElementById("valuation-analysis-report-configuration")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const navigateToReportConfiguration = (): void => {
    if (props.reportConfigurationPanelProps) {
      scrollToReportConfiguration();
      return;
    }

    if (isValuationReport) {
      const searchParams = new URLSearchParams(location.search);
      const session = searchParams.get("session");
      const realEstateId = searchParams.get("realEstateId");

      if (session && realEstateId) {
        navigate(
          `/valuation_report?session=${encodeURIComponent(session)}&realEstateId=${encodeURIComponent(realEstateId)}#report-generation-panel`,
        );
      }

      return;
    }

    if (currentValuationId) {
      navigate(`/valuations/${currentValuationId}#report-generation-panel`);
    }
  };

  return (
    <Box sx={styles.pageGrid}>
      <Box
        sx={{
          ...styles.analysisPanelsGrid,
          py: isValuationReport ? "100px" : "30px",
        }}
      >
        <Box id="valuation-analysis-property-details" sx={{ scrollMarginTop: isValuationReport ? "150px" : "90px" }}>
          <PropertyDetailsPanel valuationState={valuationState} isLoading={props.isLoading} />
        </Box>
        <Box id="valuation-analysis-market-analysis" sx={{ scrollMarginTop: isValuationReport ? "150px" : "90px" }}>
          <PricePositioningPanel
            isLoading={props.isLoading}
            onSavePriceRange={props.onSavePriceRange}
            isSaving={props.isSavingPriceRange}
          />
        </Box>
        {showDemandScore && (
          <Box id="valuation-analysis-demand-score" sx={{ scrollMarginTop: isValuationReport ? "150px" : "90px" }}>
            <DemandScorePanel isLoading={props.isLoading} valuationState={valuationState} />
          </Box>
        )}
        <Box
          id="valuation-analysis-indicative-market-valuation"
          sx={{ scrollMarginTop: isValuationReport ? "150px" : "90px" }}
        >
          <IndicativeMarketValuationPanel
            valuationState={valuationState}
            isLoading={props.isLoading}
            onSaveFactor={props.onSaveIndicativeFactor}
            isSaving={props.isSavingIndicativeFactor}
          />
        </Box>
        {props.reportConfigurationPanelProps && (
          <Box
            id="valuation-analysis-report-configuration"
            sx={{ scrollMarginTop: isValuationReport ? "150px" : "10px" }}
          >
            <ReportConfigurationPanel {...props.reportConfigurationPanelProps} />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          ...styles.summaryCard,
          py: isValuationReport ? "100px" : "30px",
        }}
      >
        <AnalysisSummaryCard
          valuationState={valuationState}
          onEditObjectData={props.onEditObjectData}
          onGenerateReport={props.onGenerateReport ?? navigateToReportConfiguration}
          onGenerateWordDocument={props.onGenerateWordDocument}
          isGenerateReportDisabled={isGenerateReportDisabled}
          isGenerateReportLoading={props.isGenerateReportLoading}
          isLoading={props.isLoading}
        />
      </Box>
    </Box>
  );
};
